import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Input } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "store/app-state";
import { toggleSearchBar } from "store/application/application-action";
import { GlobalSearch } from "components/UI/GlobalSearch";
import { decodeTags } from "utils/strings";
import {
  getTrendingSearches,
  getCreatorsSearches,
  getNichesSearches,
  getTagsSearches,
  getHistorySearches
} from "store/search/search-action";
import { clsx } from "clsx";
import { TABLET_WIDTH } from "hooks/useDeviceType";
import { ReactComponent as ArrowIcon } from "components/icons/left_arrow.svg";
import { metrics } from "utils/metrics";
import { PinkBaseButton } from "components/buttons/PinkBaseButton";
import { routes } from "routes";
import { useQuery } from "hooks/useQuery";
import "./styles/SearchInput.scss";

export const SearchInput = () => {
  const [currentSearchValue, setCurrentSearchValue] = useState("");
  const [currentTag, setCurrentTag] = useState<string[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  const isSearchBarOpen = useSelector((state: IAppState) => state.application.isSearchBarOpen);
  const isDesktop = useMediaQuery(`(min-width: ${TABLET_WIDTH}px)`);
  const dispatch = useDispatch();
  const isShowCloseBtn = isDesktop || currentSearchValue;
  const navigate = useNavigate();
  const query = useQuery();
  const location = useLocation();

  const TIMEOUT = 500;

  const { tags } = useSelector((state: IAppState) => state.search.tags);
  const { niches } = useSelector((state: IAppState) => state.search.niches);
  const { creators } = useSelector((state: IAppState) => state.search.creators);
  const currentUser = useSelector((state: IAppState) => state.user.currentUser);
  const isUserLoggedIn = currentUser && (Object.entries(currentUser).length > 0);

  const sendSearchGaEvent = async (query: string, totalCount: number) => {
    await metrics.sendSearch({
      query: query,
      totalCount: totalCount
    });
  };

  useEffect(() => {
    const match = location.pathname.match(/^\/gifs\/([^/]+)/);
    if (match !== null) {
      const tag = match[1];
      const decodedTags = decodeTags(tag);

      setCurrentTag(decodedTags);
    } else {
      setCurrentTag([]);
    }
  }, [location]);

  useEffect(() => {
    if (isSearchBarOpen && !currentSearchValue?.length) {
      dispatch(getTrendingSearches());
      if (isUserLoggedIn) {
        dispatch(getHistorySearches(""));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchBarOpen, dispatch, isUserLoggedIn]);

  useEffect(() => {
    if (!currentSearchValue) {
      return;
    }
    const search = encodeURI(currentSearchValue);
    const getData = setTimeout(() => {
      dispatch(getCreatorsSearches(search));
      dispatch(getNichesSearches(search));
      dispatch(getTagsSearches(search));
      dispatch(getHistorySearches(search));
    }, TIMEOUT);
    sendSearchGaEvent(search, creators.length + niches.length + tags.length);
    return () => clearTimeout(getData);
    // eslint-disable-next-line
  }, [currentSearchValue, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        if (isSearchBarOpen) {
          dispatch(toggleSearchBar());
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchRef, dispatch, isSearchBarOpen]);

  const handleOpenSearchBar = () => {
    if (!isSearchBarOpen) {
      dispatch(toggleSearchBar());
    }
  };

  const handleCloseAndReset = () => {
    setCurrentSearchValue("");
    setCurrentTag([]);

    if (isDesktop) {
      dispatch(toggleSearchBar());
    }
  };

  const handleCloseMobile = () => {
    dispatch(toggleSearchBar());
  };

  const searchInputHandler = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentSearchValue(value);
  };

  const removeTag = (title: string) => {
    const newInputList = currentTag.filter(tag => title !== tag);
    setCurrentTag(newInputList);

    if (!newInputList.length) {
      navigate(routes.explore());
    } else {
      navigate(routes.multipleTag(newInputList, query.get("order") ?? undefined, !!query.get("verified")));
    }
  };

  const renderTags = () => {
    if (!currentTag.length) {
      return null;
    }

    return (
      <div className="SearchInput-Tags">
        {currentTag.map(tag => (
          <PinkBaseButton key={tag} isSelected>
            {tag}
            <div onClick={() => removeTag(tag)} />
          </PinkBaseButton>
        ))}
      </div>
    );
  };

  const searchInputClsx = clsx({
    "SearchInput": true,
    "SearchInput_isOpen": isSearchBarOpen,
    "SearchInput_isWithTags": !!currentTag.length
  });

  return (
    <div className={searchInputClsx} ref={searchRef}>
      {!isDesktop && isSearchBarOpen && (
        <button className="SearchInput-CancelBtn" onClick={handleCloseMobile}>
          <ArrowIcon />
        </button>
      )}
      {/* {currentTag && <PinkBaseButton onClick={handleOpenSearchBar} isSelected>{currentTag}</PinkBaseButton>} */}
      <Input
        id="global-search-input"
        onChange={searchInputHandler}
        onSelect={handleOpenSearchBar}
        value={currentSearchValue}
        disableUnderline
        autoComplete="off"
        size="small"
        endAdornment={
          isSearchBarOpen && isShowCloseBtn && <div className="SearchInput-CloseIcon" onClick={handleCloseAndReset} />
        }
        startAdornment={
          <>
            <div
              className={clsx(["SearchInput-SearchIcon", { "SearchInput-SearchIcon_isOpen": isSearchBarOpen }])}
            />
            {renderTags()}
          </>
        }
        placeholder="Search by..."
      />
      <GlobalSearch
        setSearchInput={setCurrentSearchValue}
        currentSearchValue={currentSearchValue}
        setCurrentTag={setCurrentTag}
      />
    </div>
  );
};
